import React, { useState } from 'react';
import styled from '@emotion/styled';
import MedicalIntegrationConfigModel from '../../../../../../shared/models/organizations/MedicalIntegrationConfigModel';
import { updateModuleFieldAndFinalize } from '../../../../../../shared/utils/moduleFieldFinalize';
import { ConfigIdFieldRow, sameConfigId } from './configConnection';
import { PillError, SparkFormButton, StatusPill } from './sparkFormPills';

interface ConfigConnectionStatusProps {
    config: MedicalIntegrationConfigModel;
    rows: ConfigIdFieldRow[];
    isLoading: boolean;
    onSaved: (appId: string, configId: string) => void;
}

// The Spark form's Medical Integration Configuration ID lives on the Shared Configuration.
// Without a shared field, fall back to the locations that have their own.
const getFormRows = (rows: ConfigIdFieldRow[]) => {
    const shared = rows.filter(row => row.isShared && row.field);
    return shared.length > 0 ? shared : rows.filter(row => row.field);
};

// Shows whether this configuration is in the Spark form, or a one-click button to put it there
const ConfigConnectionStatus: React.FC<ConfigConnectionStatusProps> = ({ config, rows, isLoading, onSaved }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const formRows = getFormRows(rows);
    if (isLoading || formRows.length === 0) {
        return null;
    }

    const toUpdate = formRows.filter(row => !sameConfigId(row.field?.value, config.id));

    const updateForm = async () => {
        setIsSaving(true);
        setError(null);
        for (const row of toUpdate) {
            const result = await updateModuleFieldAndFinalize(row.app.id, row.field, config.id);
            if (result.resultType !== 'Ok') {
                setError(`Couldn't update: ${result.errors?.join(', ') || 'Unknown error'}`);
                break;
            }
            onSaved(row.app.id, config.id);
        }
        setIsSaving(false);
    };

    return (
        <Wrapper onClick={e => e.stopPropagation()}>
            {toUpdate.length === 0 ? (
                <StatusPill tone="done" title="This is the Spark form's Medical Integration Config ID. Publish to go live.">✓ Spark Config</StatusPill>
            ) : (
                <SparkFormButton type="button" onClick={updateForm} disabled={isSaving} title="Saves this ID to the Spark form's Medical Integration Config ID and syncs">
                    {isSaving ? 'Updating...' : 'Make Spark Config'}
                </SparkFormButton>
            )}
            {error && <PillError title={error}>{error}</PillError>}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    cursor: default;
`;

export default ConfigConnectionStatus;
